import React, { Suspense } from 'react';
import { defer, RouteObject, Link, redirect, Navigate } from 'react-router-dom';
import RootApp from '@/root-app/RootApp';
import RoutingErrorPage from '@/components/Error/RoutingErrorPage';
import { ROUTE_NAMES, withRoot } from '@/core/routes/routeNames';
import tokens from '@/core/tokens.json';
import store from '@/core/store/store';
import { employerRefApi } from '@/employer-ref/services/employerRefApi';
import BlobHandler, { BLOB_STORAGE_CONSTANTS } from '@/user-agreement/services/BlobHandler';
import SectionLoader from '@/components/Loader/SectionLoader';
import DashboardBreadCrumbLink from '@/dashboard/components/DashboardBreadCrumbLink';
import Dashboard from '@/dashboard/components/Dashboard/Dashboard';
import Button from '@/components/Button/Button';
import { isValidPeriodString } from '@/common/utils/date.utils';
import { getCachedCountsOrExecuteNew, isQueryCacheExist } from '@/core/network/network.utils';
import { ifMonthParamsValid } from '@/payroll-period/utils/payrollPeriod.utils';
import { dashboardApi } from '@/dashboard/services/dashboardApi';
import { payrollPeriodApi } from '@/payroll-period/services/payrollPeriodApi';
import { INITIAL_SCHEDULES_PAGE_FILTER, schedulesApi } from '@/schedules/services/schedulesApi';
import { contractsApi } from '@/contract/services/contractsApi';
import { ertnApi } from '@/ertn/services/ertnApi';
import { usersApi } from '@/users/services/usersApi';
import { employeePrintApi } from '@/employee-print/services/employeePrintApi';
import { base64ToArrayBuffer, decrypt } from '@/common/utils/encryption.utils';
import { notificationApi } from '@/notifications/services/notificationsApi';

const ImportPayrollRoot = React.lazy(() => import('@/import-payroll/ImportPayrollRoot'));
const ReviewPayrollRoot = React.lazy(() => import('@/review-payroll/ReviewPayrollRoot'));
const PayrollPeriodRoot = React.lazy(() => import('@/payroll-period/PayrollPeriodRoot'));
const EmployeeDetailsRoot = React.lazy(
  () => import('@/employee-details/components/EmployeeDetailsRoot'),
);
const SchedulesRoot = React.lazy(() => import('@/schedules/SchedulesRoot'));
const EmployeesRoot = React.lazy(() => import('@/employees/EmployeesRoot'));
const EmployerRefRoot = React.lazy(() => import('@/employer-ref/EmployerRefRoot'));
const ContractRoot = React.lazy(() => import('@/contract/ContractRoot'));
const ErtnRoot = React.lazy(() => import('@/ertn/ErtnRoot'));
const ReportsRoot = React.lazy(() => import('@/reports/ReportsRoot'));
const UsersRoot = React.lazy(() => import('@/users/UsersRoot'));
const EmployeePrintRoot = React.lazy(() => import('@/employee-print/EmployeePrintRoot'));
const UserAgreementRoot = React.lazy(() => import('@/user-agreement/UserAgreementRoot'));
const RatesRoot = React.lazy(() => import('@/rates/RatesRoot'));
const ImportRatesRoot = React.lazy(() => import('@/import-rates/ImportRatesRoot'));
const NotificationsRoot = React.lazy(() => import('@/notifications/NotificationsRoot'));
const ReconciliationRoot = React.lazy(() => import('@/reconciliation/ReconciliationRoot'));
const RetroPayRoot = React.lazy(() => import('@/retro-pay/RetroPayRoot'));
const Maintenance = React.lazy(() => import('@/maintenance/Maintenance'));

const loaderRoute = {
  path: ROUTE_NAMES.ROOT,
  Component: RootApp,
  errorElement: <RoutingErrorPage />,
  handle: {
    isFullPage: true,
  },
  children: [
    {
      index: true,
      handle: {
        isFullPage: true,
      },
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <SectionLoader />
        </React.Suspense>
      ),
    },
    {
      path: ROUTE_NAMES.NOT_FOUND,
      handle: {
        isFullPage: true,
      },
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <SectionLoader />
        </React.Suspense>
      ),
    },
  ],
};

const userAgreementIndexRoute: RouteObject = {
  index: true,
  element: <Navigate to={`${withRoot(ROUTE_NAMES.USER_AGREEMENT.ROOT)}?isFromAuth=true`} />,
};

const ratesIndexRoute: RouteObject = {
  index: true,
  element: <Navigate to={withRoot(ROUTE_NAMES.ADMINISTRATION.RATES.ROOT)} />,
};

const dashboardIndexRoute: RouteObject = {
  index: true,
  element: <Navigate to={withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD)} />,
};

const dashboardRoute: RouteObject = {
  path: ROUTE_NAMES.DASHBOARD.DASHBOARD,
  errorElement: <RoutingErrorPage />,
  handle: {
    crumb: () => <DashboardBreadCrumbLink />,
  },
  children: [
    {
      index: true,
      Component: Dashboard,
      handle: {
        header: tokens.DASHBOARD_TITLE,
      },
    },
    {
      path: ROUTE_NAMES.DASHBOARD.IMPORT_PAYROLL.ROOT,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <ImportPayrollRoot />
        </React.Suspense>
      ),
      errorElement: (
        <RoutingErrorPage
          cta={
            <Button
              component={Link}
              to={withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD)}
              variant="contained"
              title={tokens.DASHBOARD_TITLE}
              sx={{ mt: 2 }}
            >
              {tokens.DASHBOARD}
            </Button>
          }
        />
      ),
      handle: {
        header: tokens.IMPORT_PAYROLL_TITLE,
        isFullPage: true,
      },
      loader: async ({ params: { period } }) => {
        const isValidPeriod = isValidPeriodString(period);
        if (!period || !isValidPeriod) {
          return redirect(withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD));
        }

        const promise = getCachedCountsOrExecuteNew(period);
        return defer({
          response: promise,
        });
      },
    },
    {
      path: ROUTE_NAMES.DASHBOARD.IMPORT_PAYROLL.REVIEW,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <ReviewPayrollRoot />
        </React.Suspense>
      ),
      errorElement: (
        <RoutingErrorPage
          cta={
            <Button
              component={Link}
              to={withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD)}
              variant="contained"
              title={tokens.DASHBOARD_TITLE}
              sx={{ mt: 2 }}
            >
              {tokens.DASHBOARD}
            </Button>
          }
        />
      ),
      handle: {
        header: tokens.REVIEW_PAYROLL_TITLE,
        isFullPage: true,
      },
    },
    {
      path: ROUTE_NAMES.DASHBOARD.PAYROLL_PERIOD.FROM_NAV,
      children: [
        {
          index: true,
          element: <Navigate to={withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD)} />,
        },
        {
          path: ROUTE_NAMES.DASHBOARD.PAYROLL_PERIOD.FROM_DASHBOARD,
          handle: {
            header: tokens.PAYROLL_PERIODS,
            isFullPage: false,
          },
          Component: () => (
            <React.Suspense fallback={<SectionLoader />}>
              <PayrollPeriodRoot />
            </React.Suspense>
          ),
          errorElement: <RoutingErrorPage />,
          loader: async ({ params: { year, month } }) => {
            const isPeriodValid = ifMonthParamsValid(year, month);
            if (!isPeriodValid) {
              return redirect(withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD));
            }

            const schedulesPromise = store.dispatch(
              dashboardApi.endpoints.getSchedulesIndex.initiate(),
            );
            schedulesPromise.unsubscribe();

            return defer({
              response: schedulesPromise,
            });
          },
        },
        {
          path: ROUTE_NAMES.DASHBOARD.PAYROLL_PERIOD.REST,
          element: <Navigate to={withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD)} />,
        },
      ],
    },
    {
      path: ROUTE_NAMES.DASHBOARD.EMPLOYEE_DETAILS.ROOT,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <EmployeeDetailsRoot />
        </React.Suspense>
      ),
      errorElement: <RoutingErrorPage />,
      handle: {
        hideTopBar: true,
      },
      loader: async ({ params: { employeeId }, request }) => {
        const schoolYear = new URL(request.url).searchParams.get('schoolYear');
        if (
          !employeeId ||
          !schoolYear ||
          isNaN(Number(employeeId)) ||
          isNaN(Number(schoolYear)) ||
          schoolYear.length !== 6
        ) {
          return redirect(withRoot(ROUTE_NAMES.DASHBOARD.DASHBOARD));
        }

        const employeePromise = store.dispatch(
          payrollPeriodApi.endpoints.getEmployeeDetails.initiate({
            employeeId: Number(employeeId),
          }),
        );
        return defer({
          response: Promise.all([employeePromise.unwrap()]),
        });
      },
    },
  ],
};

const schedulesRoute: RouteObject = {
  path: ROUTE_NAMES.SCHEDULES.SCHEDULES,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {},
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <SchedulesRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const isSchedulesResponseCached = isQueryCacheExist(
          'getSchedulesDetails',
          INITIAL_SCHEDULES_PAGE_FILTER,
        );

        if (isSchedulesResponseCached) {
          store.dispatch(schedulesApi.util.invalidateTags(['SCHEDULES']));
        }

        const schedulesPromise = store.dispatch(
          schedulesApi.endpoints.getSchedulesDetails.initiate(INITIAL_SCHEDULES_PAGE_FILTER),
        );
        const schedulesSummariesPromise = store.dispatch(
          schedulesApi.endpoints.getSchedulesSummaries.initiate(),
        );
        const lastAcceptedPromise = store.dispatch(
          dashboardApi.endpoints.getCachedLastAcceptedBatch.initiate(),
        );
        schedulesPromise.unsubscribe();
        schedulesSummariesPromise.unsubscribe();
        lastAcceptedPromise.unsubscribe();

        return defer({
          response: Promise.all([schedulesPromise, schedulesSummariesPromise, lastAcceptedPromise]),
        });
      },
      handle: {
        header: tokens.SCHEDULES_TITLE,
      },
    },
  ],
};

const employeesRoute: RouteObject = {
  path: ROUTE_NAMES.EMPLOYEES.EMPLOYEES,
  errorElement: (
    <RoutingErrorPage
      mainDivProps={{
        minHeight: undefined,
        flex: 1,
      }}
    />
  ),
  handle: {
    header: tokens.EMPLOYEES,
    isFullPage: false,
  },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <EmployeesRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const schedulesIndexPromise = store.dispatch(
          dashboardApi.endpoints.getSchedulesIndex.initiate(),
        );
        const getBatchStatusV2 = store.dispatch(
          dashboardApi.endpoints.getUnacceptedBatches.initiate(),
        );
        schedulesIndexPromise.unsubscribe();

        return defer({
          response: Promise.all([schedulesIndexPromise, getBatchStatusV2]),
        });
      },
    },
  ],
};

const contractRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.CONTRACT,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {},
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <ContractRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const contractPromise = store.dispatch(
          contractsApi.endpoints.getContracts.initiate({
            status: 'ALL',
          }),
        );
        contractPromise.unsubscribe();

        return defer({
          response: contractPromise,
        });
      },
      handle: {
        header: tokens.CONTRACTS_TITLE,
      },
    },
  ],
};

const employerRefRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.EMPLOYER_REF,
  errorElement: <RoutingErrorPage />,
  handle: {},
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <EmployerRefRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const employerRefInfoPromise = store.dispatch(
          employerRefApi.endpoints.getEmployerRefInfo.initiate(),
        );

        return defer({
          response: employerRefInfoPromise,
        });
      },
      handle: {
        header: tokens.EMPLOYER_REF_TITLE,
        hideTopBar: true,
        isFullWidth: true,
      },
    },
  ],
};

const ertnRoute: RouteObject = {
  path: ROUTE_NAMES.ERTN.ROOT,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {
    header: tokens.ERTN,
    isFullPage: false,
  },
  children: [
    {
      index: true,
      Component: () => (
        <Suspense fallback={<SectionLoader />}>
          <ErtnRoot />
        </Suspense>
      ),
      loader: async () => {
        const indexPromise = store.dispatch(dashboardApi.endpoints.getSchedulesIndex.initiate());
        const getAllErtnsPromise = store.dispatch(
          ertnApi.endpoints.getIfActiveBatchPresent.initiate(),
        );
        getAllErtnsPromise.unsubscribe();
        indexPromise.unsubscribe();

        return defer({
          response: Promise.all([indexPromise, getAllErtnsPromise]),
        });
      },
    },
  ],
};

const usersRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.USERS,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {},
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <UsersRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const usersPromise = store.dispatch(usersApi.endpoints.getUsers.initiate());

        usersPromise.unsubscribe();

        return defer({
          response: usersPromise,
        });
      },
      handle: {
        header: tokens.USERS_TITLE,
      },
    },
  ],
};

const reportsRoute: RouteObject = {
  path: ROUTE_NAMES.REPORTS.ROOT,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {
    header: tokens.REPORTS,
    isFullPage: false,
  },
  children: [
    {
      index: true,
      Component: () => (
        <Suspense fallback={<SectionLoader />}>
          <ReportsRoot />
        </Suspense>
      ),
    },
  ],
};

const employeePrintRoute: RouteObject = {
  path: ROUTE_NAMES.EMPLOYEE_PRINT.ROOT,
  errorElement: <RoutingErrorPage />,
  handle: {
    isFullPage: true,
  },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <EmployeePrintRoot />
        </React.Suspense>
      ),
      loader: async ({ request }) => {
        const searchParams = new URL(request.url).searchParams;
        const searchParamsDecoded = decodeURIComponent(searchParams.get('params') ?? '');
        const encryptedSearchParams = base64ToArrayBuffer(searchParamsDecoded);
        const decryptedSearchParams = await decrypt(encryptedSearchParams);
        const params = new URLSearchParams(decryptedSearchParams);
        const schoolYear = params.get('schoolYear');
        const sin = params.get('sin');
        const includeDR = params.get('includeDR');
        if (
          !schoolYear ||
          !sin ||
          isNaN(Number(schoolYear)) ||
          schoolYear.length !== 4 ||
          !includeDR
        ) {
          return redirect(withRoot(ROUTE_NAMES.REPORTS.ROOT));
        }
        const employeePrintPromise = store.dispatch(
          employeePrintApi.endpoints.getEmployeePrintReport.initiate({
            sin,
            schoolYear,
            includeDR: includeDR === 'true',
          }),
        );

        return defer({
          response: employeePrintPromise,
        });
      },
      handle: {
        header: tokens.EMPLOYEE_PRINT_TITLE,
        hideTopBar: true,
        isFullWidth: true,
      },
    },
  ],
};

const ratesRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.RATES.ROOT,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: {
    header: tokens.MENU_ITEMS.ADMINISTRATION.items.RATES,
  },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <RatesRoot />
        </React.Suspense>
      ),
      handle: {
        header: tokens.MENU_ITEMS.ADMINISTRATION.items.RATES,
      },
    },
    {
      path: ROUTE_NAMES.ADMINISTRATION.RATES.IMPORT,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <ImportRatesRoot />
        </React.Suspense>
      ),
      errorElement: (
        <RoutingErrorPage
          cta={
            <Button
              component={Link}
              to={withRoot(ROUTE_NAMES.ADMINISTRATION.RATES.ROOT)}
              variant="contained"
              title={tokens.RATES_TITLE}
              sx={{ mt: 2 }}
            >
              {tokens.RATES}
            </Button>
          }
        />
      ),
      handle: {
        header: tokens.IMPORT_RATES_TITLE,
        isFullPage: true,
      },
    },
  ],
};

const notificationsRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.NOTIFICATIONS,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: { header: tokens.MENU_ITEMS.ADMINISTRATION.items.NOTIFICATIONS },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <NotificationsRoot />
        </React.Suspense>
      ),
      loader: async () => {
        const notificationsPromise = store.dispatch(
          notificationApi.endpoints.getNotificationMessage.initiate(),
        );

        return defer({
          response: notificationsPromise,
        });
      },
      handle: {
        header: tokens.NOTIFICATIONS_TITLE,
        hideTopBar: true,
        isFullWidth: true,
      },
    },
  ],
};

const reconciliationRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.RECONCILIATION,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: { header: tokens.MENU_ITEMS.ADMINISTRATION.items.RECONCILIATION },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <ReconciliationRoot />
        </React.Suspense>
      ),
      handle: {
        header: tokens.NOTIFICATIONS_TITLE,
        hideTopBar: true,
        isFullWidth: true,
      },
    },
  ],
};

const retroPayRoute: RouteObject = {
  path: ROUTE_NAMES.ADMINISTRATION.RETRO_PAY,
  errorElement: <RoutingErrorPage mainDivProps={{ minHeight: undefined, flex: 1 }} />,
  handle: { header: tokens.MENU_ITEMS.ADMINISTRATION.items.RETRO_PAY },
  children: [
    {
      index: true,
      Component: () => (
        <React.Suspense fallback={<SectionLoader />}>
          <RetroPayRoot />
        </React.Suspense>
      ),
      handle: {
        header: tokens.NOTIFICATIONS_TITLE,
        hideTopBar: true,
        isFullWidth: true,
      },
    },
  ],
};

const userAgreementRoute: RouteObject = {
  path: ROUTE_NAMES.USER_AGREEMENT.ROOT,
  errorElement: <RoutingErrorPage />,
  handle: {
    header: tokens.USER_AGREEMENT,
    isFullPage: true,
    useSecondaryBackground: true,
  },
  children: [
    {
      index: true,
      Component: () => (
        <Suspense fallback={<SectionLoader />}>
          <UserAgreementRoot />
        </Suspense>
      ),
      loader: async () => {
        const blobHandler = new BlobHandler(BLOB_STORAGE_CONSTANTS.STORAGE_ACCOUNT.USER_AGREEMENT);
        const userAgreementPromise = blobHandler.getBlob(
          BLOB_STORAGE_CONSTANTS.CONTAINER.USER_AGREEMENT,
          BLOB_STORAGE_CONSTANTS.BLOB.USER_AGREEMENT,
        );

        const employerRefInfoPromise = store.dispatch(
          employerRefApi.endpoints.getEmployerRefInfo.initiate(),
        );

        return defer({
          userAgreementResponse: userAgreementPromise,
          employerRefInfoResponse: employerRefInfoPromise,
        });
      },
    },
  ],
};

const maintenanceRoute: RouteObject = {
  path: ROUTE_NAMES.MAINTENANCE.MAIN,
  errorElement: <RoutingErrorPage />,
  handle: {
    header: tokens.MAINTENANCE,
    isFullPage: true,
    useSecondaryBackground: true,
  },
  children: [
    {
      index: true,
      Component: () => (
        <Suspense fallback={<SectionLoader />}>
          <Maintenance />
        </Suspense>
      ),
    },
  ],
};

export {
  loaderRoute,
  userAgreementIndexRoute,
  ratesIndexRoute,
  dashboardIndexRoute,
  dashboardRoute,
  schedulesRoute,
  employeesRoute,
  contractRoute,
  employerRefRoute,
  ertnRoute,
  usersRoute,
  reportsRoute,
  employeePrintRoute,
  ratesRoute,
  userAgreementRoute,
  notificationsRoute,
  reconciliationRoute,
  retroPayRoute,
  maintenanceRoute,
};
